import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

export function usePushNotifications(token) {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');

    // De browser weten dát hij een abonnement heeft is niet hetzelfde als de server
    // die kennen. Op 12-08-2026 stond de knop op "Meldingen uit" (= geabonneerd)
    // terwijl push_subscriptions leeg was: de eerste registratie was met 422
    // geweigerd, maar het abonnement bleef wél in Chrome staan. Zo lijkt een kapotte
    // koppeling precies op een werkende.
    //
    // Daarom bij elke start opnieuw aanmelden bij de server als er lokaal een
    // abonnement is. Dat is idempotent (de server doet updateOrCreate op de
    // endpoint-hash) en herstelt zichzelf zodra de server weer bereikbaar is.
    const checkSubscription = useCallback(async () => {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                setIsSubscribed(false);
                return;
            }

            try {
                await axios.post(
                    `${API_URL}/api/push/subscribe`,
                    subscription.toJSON(),
                    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                );
                setIsSubscribed(true);
            } catch (e) {
                // Lokaal wél, bij de server niet: dan is er geen werkende melding.
                console.error('Push: abonnement staat in de browser maar de server weigert het', e);
                setIsSubscribed(false);
            }
        } catch (e) {
            console.error('Check subscription error:', e);
        }
    }, [token]);

    // `token` hoort erbij: de sync met de server heeft hem nodig, en bij de eerste
    // render is hij vaak nog leeg.
    useEffect(() => {
        const supported = 'serviceWorker' in navigator && 'PushManager' in window;
        setIsSupported(supported);

        if (supported) {
            setPermission(Notification.permission);
            checkSubscription();
        }
    }, [checkSubscription]);

    const subscribe = useCallback(async () => {
        if (!token) return false;

        try {
            // Request permission
            const perm = await Notification.requestPermission();
            setPermission(perm);

            if (perm !== 'granted') {
                return false;
            }

            // Get VAPID public key
            const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`);
            const vapidPublicKey = data.publicKey;

            // Convert VAPID key
            const urlBase64ToUint8Array = (base64String) => {
                const padding = '='.repeat((4 - base64String.length % 4) % 4);
                const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                const rawData = window.atob(base64);
                const outputArray = new Uint8Array(rawData.length);
                for (let i = 0; i < rawData.length; ++i) {
                    outputArray[i] = rawData.charCodeAt(i);
                }
                return outputArray;
            };

            // Subscribe
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            // Send subscription to server
            // Plat versturen, niet genest onder `subscription`. De server valideert
            // `endpoint` en `keys.p256dh`/`keys.auth` op het topniveau (SubscribePushRequest),
            // dus een extra laag gaf 422 en het abonnement kwam nooit aan. Gemeten
            // 12-08-2026: toestemming stond in Chrome, push_subscriptions bleef leeg.
            await axios.post(
                `${API_URL}/api/push/subscribe`,
                subscription.toJSON(),
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setIsSubscribed(true);
            return true;
        } catch (e) {
            console.error('Subscribe error:', e);
            return false;
        }
    }, [token]);

    const unsubscribe = useCallback(async () => {
        if (!token) return false;

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await subscription.unsubscribe();
            }

            await axios.post(
                `${API_URL}/api/push/unsubscribe`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setIsSubscribed(false);
            return true;
        } catch (e) {
            console.error('Unsubscribe error:', e);
            return false;
        }
    }, [token]);

    return {
        isSupported,
        isSubscribed,
        permission,
        subscribe,
        unsubscribe
    };
}
